// ARCA-82 WS2 — Modal/overlay consolidation core (UI-SPEC §5 modal FSM).
//
// The shared backdrop + panel for the two canonical take-two overlay flavors
// (Docs/UI-prototype take two/project/src/ui.jsx Sheet/Dialog +
// MatchOverlays.jsx). Every product modal renders through <Sheet> or <Dialog>
// (thin wrappers over this), so the scrim colour, grab handle, entrance motion,
// focus trap, and dismissal contract live in exactly ONE place — no per-modal
// inline scrim/panel styling, and NO close-x.
//
// Composition (pieces.css .scrim/.sheet/.dialog recipes):
//   - .scrim          full-viewport backdrop (rgba(59,42,28,.42) + blur). The
//                     role="dialog" host + focus-trap ref live here (matches the
//                     pre-WS2 app pattern, so [role="dialog"] backdrop-tap tests
//                     keep working). `.center` switches sheet → centered dialog.
//   - .sheet/.dialog  the panel. Sheets get a 44×5 grab handle as their visible
//                     dismiss affordance; dialogs pop in centered.
//
// A11y contract (UI-SPEC §9): role="dialog" + aria-modal + aria-labelledby/
// describedby + useFocusTrap (Escape + Tab cycle + initial focus + restore).
// Dismissal is scrim-tap (pointer) + Escape (keyboard) — never a close-x.

import { type CSSProperties, type JSX, type ReactNode, useRef } from 'react'

import { useFocusTrap } from './useFocusTrap'

export type ModalVariant = 'sheet' | 'dialog'

export interface ModalScrimProps {
  readonly variant: ModalVariant
  readonly onClose: () => void
  readonly labelledBy?: string
  readonly describedBy?: string
  readonly testId?: string
  /**
   * Whether tapping the backdrop dismisses. Default true. Set false for flows
   * that must not be lost on an accidental tap (e.g. a submitting form). Escape
   * is independently gated by the caller's onClose, so pass a no-op onClose to
   * fully lock the modal open.
   */
  readonly dismissOnScrimTap?: boolean
  /** Extra classes appended to the .scrim host (e.g. screen-shroud hooks). */
  readonly scrimClassName?: string
  /** Inline overrides for the .scrim host (e.g. a bespoke z-index layer). */
  readonly scrimStyle?: CSSProperties
  /** Extra classes appended to the panel (.sheet/.dialog). */
  readonly panelClassName?: string
  /** Inline overrides for the panel (e.g. a scrollable column max-height). */
  readonly panelStyle?: CSSProperties
  readonly children: ReactNode
}

export function ModalScrim({
  variant,
  onClose,
  labelledBy,
  describedBy,
  testId,
  dismissOnScrimTap = true,
  scrimClassName,
  scrimStyle,
  panelClassName,
  panelStyle,
  children,
}: ModalScrimProps): JSX.Element {
  const scrimRef = useRef<HTMLDivElement>(null)
  // Escape + Tab trap + initial focus + restore. The modal is always "open"
  // while mounted — the parent owns conditional mounting.
  useFocusTrap(scrimRef, true, onClose)

  const handleScrimClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    // Only the backdrop itself dismisses — never a click bubbling up from a
    // child (target !== currentTarget), so panel content is safe to click.
    if (dismissOnScrimTap && e.target === e.currentTarget) onClose()
  }

  const scrimClasses = [
    'scrim',
    variant === 'dialog' ? 'center' : null,
    scrimClassName,
  ]
    .filter(Boolean)
    .join(' ')

  const panelClasses = [variant, panelClassName].filter(Boolean).join(' ')

  return (
    // Backdrop tap-to-dismiss is a redundant pointer affordance; Escape
    // (useFocusTrap) is the keyboard equivalent, and the handler only fires when
    // the target IS the scrim (never a focusable child), so keyboard users are
    // never trapped on it.
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape is the keyboard dismiss path (useFocusTrap).
    <div
      ref={scrimRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      data-testid={testId}
      className={scrimClasses}
      style={scrimStyle}
      onClick={handleScrimClick}
    >
      <div className={panelClasses} style={panelStyle}>
        {variant === 'sheet' ? (
          <div className="sheet-grab" aria-hidden="true" />
        ) : null}
        {children}
      </div>
    </div>
  )
}
